'use client';
import React from 'react';
import { X, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import { ALL_COMPONENTS } from '@/lib/archsim-data';
import type { CanvasNode } from '@/lib/archsim-data';
import type { SimulationState } from './ArchitectureCanvasScreen';

interface BottleneckPanelProps {
  nodes: CanvasNode[];
  simState: SimulationState;
  onClose: () => void;
}

interface BottleneckEntry {
  nodeId: string;
  label: string;
  icon: string;
  cpuLoad: number;
  latency: number;
  errorRate: number;
  severity: 'critical' | 'warning' | 'healthy';
  recommendation: string;
}

function getSeverity(node: CanvasNode): 'critical' | 'warning' | 'healthy' {
  if (node.metrics.cpuLoad > 90 || node.metrics.errorRate > 3) return 'critical';
  if (node.metrics.cpuLoad > 70 || node.metrics.errorRate > 1) return 'warning';
  return 'healthy';
}

function getRecommendation(node: CanvasNode, defId: string): string {
  const cpu = node.metrics.cpuLoad;
  if (defId.includes('db') || defId === 'postgres' || defId === 'mysql' || defId === 'mongodb') {
    if (cpu > 80) return 'Add read replicas to distribute query load';
    return 'Consider connection pooling and query optimization';
  }
  if (defId === 'load-balancer') {
    if (cpu > 80) return 'Scale horizontally — add more load balancer instances';
    return 'Enable health checks and connection draining';
  }
  if (defId === 'redis' || defId === 'memcached') {
    return 'Monitor eviction rate — consider increasing memory allocation';
  }
  if (cpu > 85) return 'Scale out — add more instances behind load balancer';
  if (cpu > 70) return 'Optimize hot code paths or increase instance size';
  return 'Performance within acceptable range';
}

export default function BottleneckPanel({ nodes, simState, onClose }: BottleneckPanelProps) {
  const isActive = simState !== 'stopped';

  const bottlenecks: BottleneckEntry[] = nodes
    .filter(n => isActive && n.metrics.cpuLoad > 0)
    .map(n => {
      const def = ALL_COMPONENTS.find(c => c.id === n.defId);
      return {
        nodeId: n.id,
        label: n.label,
        icon: def?.icon ?? '⬡',
        cpuLoad: n.metrics.cpuLoad,
        latency: n.metrics.latency,
        errorRate: n.metrics.errorRate,
        severity: getSeverity(n),
        recommendation: getRecommendation(n, n.defId),
      };
    })
    .sort((a, b) => b.cpuLoad - a.cpuLoad)
    .slice(0, 6);

  const criticals = bottlenecks.filter(b => b.severity === 'critical');
  const warnings = bottlenecks.filter(b => b.severity === 'warning');

  return (
    <div className="w-72 panel-glass rounded-xl overflow-hidden animate-scale-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className={criticals.length > 0 ? 'text-danger' : warnings.length > 0 ? 'text-warning' : 'text-positive'} />
          <span className="text-sm font-semibold text-foreground">Bottleneck Analysis</span>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all">
          <X size={12} />
        </button>
      </div>

      {/* Summary bar */}
      {isActive && (
        <div className="flex border-b border-border">
          <div className="flex-1 flex flex-col items-center py-2 border-r border-border">
            <span className="text-lg font-bold text-danger font-mono-nums">{criticals.length}</span>
            <span className="text-xs text-muted-foreground">Critical</span>
          </div>
          <div className="flex-1 flex flex-col items-center py-2 border-r border-border">
            <span className="text-lg font-bold text-warning font-mono-nums">{warnings.length}</span>
            <span className="text-xs text-muted-foreground">Warning</span>
          </div>
          <div className="flex-1 flex flex-col items-center py-2">
            <span className="text-lg font-bold text-positive font-mono-nums">
              {bottlenecks.filter(b => b.severity === 'healthy').length}
            </span>
            <span className="text-xs text-muted-foreground">Healthy</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-h-80 overflow-y-auto scrollbar-thin">
        {!isActive ? (
          <div className="p-6 text-center">
            <TrendingUp size={24} className="text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Run the simulation to see bottleneck analysis</p>
          </div>
        ) : bottlenecks.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle size={24} className="text-positive mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No bottlenecks detected — system is healthy</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {bottlenecks.map((b, i) => (
              <div key={`bottleneck-${b.nodeId}`} className={`px-4 py-3 ${
                b.severity === 'critical' ? 'bg-danger/5' :
                b.severity === 'warning' ? 'bg-warning/5' : ''
              }`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{b.icon}</span>
                    <span className="text-xs font-semibold text-foreground truncate max-w-[120px]">{b.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-bold font-mono-nums ${
                      b.severity === 'critical' ? 'text-danger' :
                      b.severity === 'warning' ? 'text-warning' : 'text-positive'
                    }`}>
                      {b.cpuLoad}%
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      b.severity === 'critical' ? 'bg-danger' :
                      b.severity === 'warning' ? 'bg-warning' : 'bg-positive'
                    }`} />
                  </div>
                </div>

                {/* CPU bar */}
                <div className="h-1 bg-secondary rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      b.severity === 'critical' ? 'bg-danger' :
                      b.severity === 'warning' ? 'bg-warning' : 'bg-positive'
                    }`}
                    style={{ width: `${b.cpuLoad}%` }}
                  />
                </div>

                <div className="flex gap-3 mb-1.5">
                  <span className="text-xs text-muted-foreground">
                    <span className="text-foreground font-mono-nums">{b.latency}ms</span> p99
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <span className={`font-mono-nums ${b.errorRate > 1 ? 'text-danger' : 'text-foreground'}`}>{b.errorRate}%</span> err
                  </span>
                </div>

                <p className="text-xs text-muted-foreground/80 leading-relaxed">{b.recommendation}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}